'use client';

import { ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip as RadixTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface TooltipProps {
  content: string | { title?: string; description: string };
  children?: ReactNode;
  className?: string;
  iconClassName?: string;
  showArrow?: boolean;
}

export function Tooltip({ 
  content, 
  children,
  className,
  iconClassName,
  showArrow = true
}: TooltipProps) {
  const isRichContent = typeof content === 'object';
  
  return (
    <TooltipProvider delayDuration={300}>
      <RadixTooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center cursor-help">
            {children || (
              <span className="inline-flex p-0.5">
                <HelpCircle className={cn(
                  "w-4 h-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors",
                  iconClassName
                )} />
              </span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent 
          className={cn(
            "text-white",
            className
          )}
          sideOffset={6}
          showArrow={true}
        >
          {isRichContent ? (
            <div className="space-y-1">
              {content.title && (
                <div className="font-semibold text-sm text-white">
                  {content.title}
                </div>
              )}
              <div className="text-xs text-white/90 leading-relaxed">
                {content.description}
              </div>
            </div>
          ) : (
            <div className="text-xs text-white leading-relaxed">{content}</div>
          )}
        </TooltipContent>
      </RadixTooltip>
    </TooltipProvider>
  );
}

interface InfoTooltipProps {
  content: string | { title?: string; description: string };
  className?: string;
}

export function InfoTooltip({ content, className }: InfoTooltipProps) {
  return (
    <Tooltip 
      content={content} 
      className={className}
      iconClassName="ml-1 inline-block align-text-bottom"
    />
  );
}